// reCAPTCHA v3: invisible, scored by the backend's api.recaptcha(action).
// Off only when page and API are both local: a live API still rejects a sign-in with no token.
const LOCAL_URL = /^https?:\/\/(localhost|127\.0\.0\.1)(:|\/|$)/;
const IS_LOCAL_SETUP =
  LOCAL_URL.test(globalThis.location?.origin ?? "") &&
  LOCAL_URL.test(process.env.REACT_APP_API_BASE_URL ?? "");
const SITE_KEY = IS_LOCAL_SETUP
  ? undefined
  : (process.env.REACT_APP_RECAPTCHA_SITE_KEY as string | undefined);

// A host the key does not list may never answer, and the sign-in must still reach the backend.
const TOKEN_TIMEOUT_MS = 8000;

type Grecaptcha = {
  ready: (cb: () => void) => void;
  execute: (siteKey: string, options: { action: string }) => Promise<string>;
};

declare global {
  interface Window {
    grecaptcha?: Grecaptcha;
  }
}

let loading: Promise<Grecaptcha> | null = null;

const load = (key: string): Promise<Grecaptcha> => {
  loading ??= new Promise<Grecaptcha>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(key)}`;
    script.async = true;
    script.onload = () => window.grecaptcha?.ready(() => resolve(window.grecaptcha as Grecaptcha));
    script.onerror = () => {
      loading = null;
      script.remove();
      reject(new Error("reCAPTCHA failed to load. Check your connection and try again."));
    };
    document.head.appendChild(script);
  });
  return loading;
};

/** Loads the script early so v3 can watch the page before the form is sent. */
export const preloadRecaptcha = () => {
  if (SITE_KEY) load(SITE_KEY).catch(() => undefined);
};

/**
 * Token for `action`; empty when there is no site key, or when Google gives none (blocked, or a
 * host the key does not list). The backend decides: a whitelisted account signs in without one.
 */
export const getRecaptchaToken = async (action: string): Promise<string> => {
  const key = SITE_KEY;
  if (!key) return "";
  try {
    const timeout = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error("timeout")), TOKEN_TIMEOUT_MS),
    );
    return await Promise.race([load(key).then((api) => api.execute(key, { action })), timeout]);
  } catch {
    return "";
  }
};
